<template>
  <v-card>
    <v-card-title
      ><span class="headline font-weight-light">
        {{ title }}</span
      ></v-card-title
    >
    <v-divider v-if="title !== ''"></v-divider>
    <v-card-text>
      <v-alert density="compact" type="info" v-if="layer === 'lookup'"
        >Enter the following information from your receipt.</v-alert
      >
      <v-alert density="compact" type="info" v-if="layer === 'create'"
        >Enter the following information to create your account.</v-alert
      >
      <v-alert
        density="compact"
        type="error"
        v-if="errorMessage && !!errorMessage.trim()"
        >{{ errorMessage }}</v-alert
      >
      <v-form v-model="isValid" ref="form">
        <v-text-field
          v-model="accountNumber"
          label="Account Number"
          :disabled="isLoading"
          @change="resetErrors('accountNumber')"
          :error-messages="accountNumberErrors"
          :rules="[(v) => (v && !!v.trim()) || 'Account Number is required']"
          required
          @keyup.enter="searchAndAdd"
          v-if="layer === 'lookup'"
        ></v-text-field>
        <v-text-field
          v-model="storeNumber"
          label="Store Number"
          @change="resetErrors('storeNumber')"
          :error-messages="storeNumberErrors"
          :disabled="isLoading"
          :rules="[(v) => (v && !!v.trim()) || 'Store Number is required']"
          required
          @keyup.enter="searchAndAdd"
          v-if="layer === 'lookup'"
        ></v-text-field>
        <v-text-field
          v-model="invoiceNumber"
          label="Invoice Number"
          @change="resetErrors('invoiceNumber')"
          :error-messages="invoiceNumberErrors"
          :disabled="isLoading"
          :rules="[(v) => (v && !!v.trim()) || 'Invoice Number is required']"
          required
          class="mb-5"
          @keyup.enter="searchAndAdd"
          v-if="layer === 'lookup'"
        ></v-text-field>
        <v-text-field
          v-model="username"
          label="Username"
          :disabled="isLoading"
          @change="resetErrors('username')"
          :error-messages="usernameErrors"
          :rules="[
            (v) => (v && !!v.trim()) || 'Username is required',
            (v) => /^([a-zA-Z0-9_\-\.@]+)$/.test(v) || 'Username is invalid',
            (v) =>
              (v || '').length <= 50 ||
              'Username cannot be more than 50 characters',
          ]"
          required
          @keyup.enter="createWebAccount"
          v-if="layer === 'create'"
        ></v-text-field>
        <v-text-field
          v-model="displayName"
          label="Display Name"
          :disabled="isLoading"
          @change="resetErrors('displayName')"
          :error-messages="displayNameErrors"
          :rules="[
            (v) => (v && !!v.trim()) || 'Display name is required',
            (v) =>
              (v || '').length <= 50 ||
              'Display name cannot be more than 50 characters',
          ]"
          required
          @keyup.enter="createWebAccount"
          v-if="layer === 'create'"
        ></v-text-field>
        <v-text-field
          v-model="emailAddress"
          label="Email"
          @change="resetErrors('emailAddress')"
          :error-messages="emailAddressErrors"
          :disabled="isLoading"
          :rules="[
            (v) => (v && !!v.trim()) || 'Email is required',
            (v) =>
              emailRegEx.test(v) ||
              'Email is invalid. Format must be user@example.com',
            (v) =>
              (v || '').length <= 50 ||
              'Email cannot be more than 50 characters',
          ]"
          required
          class="mb-5"
          @keyup.enter="createWebAccount"
          v-if="layer === 'create'"
        ></v-text-field>
      </v-form>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-btn v-track="'account.createAccountForm.cancel'" @click="$emit('onCancel')" variant="text">Cancel</v-btn>
      <v-btn
        color="accent"
        :disabled="isLoading"
        v-track="'account.createAccountForm.searchAndAdd'"
        @click="searchAndAdd"
        v-if="layer === 'lookup'"
      >
        {{ buttonTitle }}
        <v-progress-circular
          v-if="isLoading"
          class="ml-2"
          :size="20"
          :width="2"
          indeterminate
        ></v-progress-circular>
      </v-btn>
      <v-btn
        color="accent"
        :disabled="isLoading"
        v-track="'account.createAccountForm.createWebAccount'"
        @click="createWebAccount"
        v-if="layer === 'create'"
        >Create Account
        <v-progress-circular
          v-if="isLoading"
          class="ml-2"
          :size="20"
          :width="2"
          indeterminate
        ></v-progress-circular
      ></v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { emailExpression } from "@/helpers/Validation/ValidationHelpers";
import type UnprocessableEntityResponse from "@/interfaces/Repository/UnprocessableEntityResponse";
import type WebLoginAccount from "@/interfaces/WebLogin/WebLoginAccount";
import { addAccount } from "@/repositories/Web/WebMapRepository";
import {
  createWebLogin,
  verifyCustomerByReceipt,
} from "@/repositories/Web/WebLoginRepository";

const props = withDefaults(
  defineProps<{
    isAddingAccountToWebLogin?: boolean;
    onChanged?: boolean;
    title?: string;
  }>(),
  {
    isAddingAccountToWebLogin: false,
    onChanged: false,
    title: "",
  }
);

const emit = defineEmits<{
  onCancel: [];
  onAdded: [account: WebLoginAccount];
  onCreated: [];
}>();

const form = ref<{
  validate: () => Promise<{ valid: boolean }> | boolean;
  reset: () => void;
} | null>(null);
const isLoading = ref(false);
const isValid = ref(true);
const errorMessage = ref("");
const accountNumber = ref("");
const storeNumber = ref("");
const invoiceNumber = ref("");
const accountNumberErrors = ref<string[]>([]);
const storeNumberErrors = ref<string[]>([]);
const invoiceNumberErrors = ref<string[]>([]);
const username = ref("");
const displayName = ref("");
const emailAddress = ref("");
const usernameErrors = ref<string[]>([]);
const displayNameErrors = ref<string[]>([]);
const emailAddressErrors = ref<string[]>([]);
const layer = ref("lookup");
const emailRegEx = emailExpression;

const buttonTitle = computed(() =>
  `${props.isAddingAccountToWebLogin ? "Add" : "Search For"} Account`
);

function resetErrors(type = ""): void {
  if (type === "" || type === "accountNumber") {
    accountNumberErrors.value = [];
  }
  if (type === "" || type === "invoiceNumber") {
    invoiceNumberErrors.value = [];
  }
  if (type === "" || type === "storeNumber") {
    storeNumberErrors.value = [];
  }
  if (type === "" || type === "username") {
    usernameErrors.value = [];
  }
  if (type === "" || type === "displayName") {
    displayNameErrors.value = [];
  }
  if (type === "" || type === "emailAddress") {
    emailAddressErrors.value = [];
  }

  errorMessage.value = "";
}

async function searchAndAdd(): Promise<void> {
  resetErrors();
  const validationResult = await form.value?.validate();
  isValid.value =
    typeof validationResult === "boolean"
      ? validationResult
      : validationResult?.valid ?? isValid.value;

  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  try {
    const response = await verifyCustomerByReceipt(
      accountNumber.value,
      storeNumber.value,
      invoiceNumber.value
    );

    if (response.status === 422) {
      const errors = response.data as UnprocessableEntityResponse[];
      accountNumberErrors.value = errors
        .filter((error) => error.property === "accountNumber")
        .map((error) => error.message);
      storeNumberErrors.value = errors
        .filter((error) => error.property === "storeNumber")
        .map((error) => error.message);
      invoiceNumberErrors.value = errors
        .filter((error) => error.property === "invoiceNumber")
        .map((error) => error.message);
      errorMessage.value =
        errors.find((error) => error.property === "")?.message ?? "";
      return;
    }

    if (!props.isAddingAccountToWebLogin) {
      const selectedAccount = accountNumber.value;
      const selectedStore = storeNumber.value;
      const selectedInvoice = invoiceNumber.value;
      form.value?.reset();
      layer.value = "create";
      accountNumber.value = selectedAccount;
      storeNumber.value = selectedStore;
      invoiceNumber.value = selectedInvoice;
      return;
    }

    await addAccountToWebLogin();
  } finally {
    isLoading.value = false;
  }
}

async function addAccountToWebLogin(): Promise<void> {
  const response = await addAccount(
    storeNumber.value,
    accountNumber.value,
    invoiceNumber.value
  );

  if (response.status === 422) {
    const errors = response.data as UnprocessableEntityResponse[];
    accountNumberErrors.value = errors
      .filter((error) => error.property === "AccountId")
      .map((error) => error.message);
    return;
  }

  const account = response.data as unknown as WebLoginAccount;
  form.value?.reset();
  emit("onAdded", account);
}

async function createWebAccount(): Promise<void> {
  resetErrors();
  const validationResult = await form.value?.validate();
  isValid.value =
    typeof validationResult === "boolean"
      ? validationResult
      : validationResult?.valid ?? isValid.value;

  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  try {
    const response = await createWebLogin(
      username.value,
      displayName.value,
      emailAddress.value,
      storeNumber.value,
      accountNumber.value,
      invoiceNumber.value
    );

    if (response.status === 422) {
      const errors = response.data as UnprocessableEntityResponse[];
      usernameErrors.value = errors
        .filter((error) => error.property === "Username")
        .map((error) => error.message);
      displayNameErrors.value = errors
        .filter((error) => error.property === "DisplayName")
        .map((error) => error.message);
      emailAddressErrors.value = errors
        .filter((error) => error.property === "Email")
        .map((error) => error.message);
      errorMessage.value =
        errors.find((error) => error.property === "")?.message ?? "";
      return;
    }

    emit("onCreated");
  } finally {
    isLoading.value = false;
  }
}

function reset(): void {
  form.value?.reset();
  layer.value = "lookup";
  resetErrors();
}

watch(
  () => props.onChanged,
  () => {
    reset();
  }
);
</script>
